import { useZero } from "@rocicorp/zero/react";
import { type FormEvent, useState } from "react";
import { jitUserProvisioningEnabled } from "../../../../shared/jitProvisioning";
import { sharedMutators } from "../../../../shared/mutators";
import { generateId } from "../../../../shared/id";
import { ssoIssuerUrlError } from "../../../../shared/ssoIssuerUrl";
import { SSO_PROVIDER_ID_PATTERN } from "../../../../shared/ssoProviderIds";
import { Box } from "../../Box";
import { Button } from "../../Button";
import { Dropdown } from "../../Dropdown";
import { Icon } from "../../Icon";
import { Input } from "../../Input";
import { Modal } from "../../Modal";
import { SettingsModalBody, SettingsModalFooter } from "../../SettingsLayout";
import { Text } from "../../Text";
import { DropdownOpener, settingsModalStyle } from "../shared";
import {
  DEFAULT_OIDC_SCOPES,
  emptySsoConfig,
  parseScopes,
  type Protocol,
  type SsoConfig,
} from "./constants";
import {
  type IdentityProvider,
  type MetadataSource,
  uniqueProviderId,
} from "./identityProviders";
import { newScimToken } from "./scimToken";
import { PrimaryChangeModal } from "./SsoProviderChanges";
import { PROTOCOL_LABELS, SsoModalForm, SsoModalHeader } from "./SsoModal";
import { GuideToggle, SsoProviderSteps } from "./SsoProviderSteps";
import { Field } from "./shared";

export function NewSsoProvider({
  idp,
  takenProviderIds,
  onGeneratedScimToken,
  onClose,
}: {
  idp: IdentityProvider;
  takenProviderIds: string[];
  onGeneratedScimToken: (token: string | null) => void;
  onClose: () => void;
}) {
  const z = useZero();
  const protocols = (["oidc", "saml"] as const).filter((p) => idp.protocols[p]);
  // A default suggestion only; the admin can pick any id. "oidc" keeps a
  // re-created first generic OIDC provider’s registered redirect URI stable.
  const defaultProviderId = (protocol: Protocol) =>
    uniqueProviderId(idp.providerId ?? protocol, takenProviderIds);
  const [config, setConfig] = useState<SsoConfig>(() => ({
    ...emptySsoConfig,
    protocol: idp.defaultProtocol,
    providerId: defaultProviderId(idp.defaultProtocol),
    scopes: DEFAULT_OIDC_SCOPES.join(" "),
    mapping: { ...(idp.protocols.saml?.mapping ?? emptySsoConfig.mapping) },
  }));
  const [metadataSource, setMetadataSource] =
    useState<MetadataSource>("metadata");
  const [showGuide, setShowGuide] = useState(true);
  const [confirmingPrimary, setConfirmingPrimary] = useState(false);

  const jit = jitUserProvisioningEnabled({ syncMode: config.syncMode });

  function chooseProtocol(protocol: Protocol) {
    setConfig((f) => ({
      ...f,
      protocol,
      // Follow the protocol's default unless the admin edited the id.
      providerId:
        f.providerId === defaultProviderId(f.protocol)
          ? defaultProviderId(protocol)
          : f.providerId,
    }));
  }

  function chooseMetadataSource(next: MetadataSource) {
    setMetadataSource(next);
    setConfig((f) => ({
      ...f,
      metadataUrl: "",
      metadataXml: "",
      entryPoint: "",
      cert: "",
    }));
  }

  async function register() {
    const scim = config.syncMode === "scim" ? await newScimToken() : null;

    const shared = {
      id: generateId(),
      providerId: config.providerId.trim(),
      issuer: config.issuer.trim(),
      domain: config.domain.trim().toLowerCase(),
      syncMode: config.syncMode,
      isPrimary: config.isPrimary,
    };
    const { image, ...requiredMapping } = config.mapping;
    const args =
      config.protocol === "oidc"
        ? {
            ...shared,
            protocol: "oidc" as const,
            clientId: config.clientId.trim(),
            clientSecret: config.clientSecret.trim(),
            scopes: parseScopes(config.scopes),
          }
        : {
            ...shared,
            protocol: "saml" as const,
            ...(config.metadataUrl.trim() && {
              metadataUrl: config.metadataUrl.trim(),
            }),
            ...(config.metadataXml.trim() && {
              metadataXml: config.metadataXml.trim(),
            }),
            ...(metadataSource === "manual" && {
              entryPoint: config.entryPoint.trim(),
              cert: config.cert.trim(),
            }),
            ...(jit && {
              mapping: {
                email: requiredMapping.email.trim(),
                firstName: requiredMapping.firstName.trim(),
                lastName: requiredMapping.lastName.trim(),
                ...(image.trim() && { image: image.trim() }),
              },
            }),
          };
    z.mutate(sharedMutators.registerSSOProvider(args));

    if (scim) {
      z.mutate(
        sharedMutators.generateScimToken({
          ssoProviderId: shared.id,
          tokenHash: scim.hash,
        })
      );
      onGeneratedScimToken(scim.token);
    }

    onClose();
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (config.isPrimary && !config.primaryReplaceConfirmed)
      setConfirmingPrimary(true);
    else void register();
  }

  const issuerError =
    config.protocol === "oidc" && config.issuer.trim()
      ? ssoIssuerUrlError(config.issuer.trim())
      : null;
  const protocolField = protocols.length > 1 && (
    <Field>
      <Box horizontal={8} vertical={10}>
        <Text variant="text-small-md">Protocol</Text>
      </Box>
      <Dropdown
        opener={
          <DropdownOpener type="button">
            <Text variant="text-regular">
              {PROTOCOL_LABELS[config.protocol]}
            </Text>
            <span style={{ opacity: 0.3 }}>
              <Icon name="chevron-down" size={12} />
            </span>
          </DropdownOpener>
        }
        align="bottom-left"
        sections={[
          {
            items: protocols.map((protocol) => ({
              type: "button" as const,
              left: null,
              label: PROTOCOL_LABELS[protocol],
              right:
                protocol === config.protocol ? (
                  <Icon name="check" size={12} color="var(--text-secondary)" />
                ) : undefined,
              onClick: () => chooseProtocol(protocol),
            })),
          },
        ]}
      />
    </Field>
  );

  return (
    <Modal open onClose={onClose} style={settingsModalStyle}>
      <SsoModalForm onSubmit={handleSubmit} autoComplete="off">
        <SsoModalHeader
          idp={idp}
          title={idp.name}
          subtitle={PROTOCOL_LABELS[config.protocol]}
          onClose={onClose}
          actions={
            <GuideToggle
              isOn={showGuide}
              onToggle={() => setShowGuide((was) => !was)}
            />
          }
        />

        <SettingsModalBody style={{ gap: 24 }} data-popover-host>
          <SsoProviderSteps
            idp={idp}
            config={config}
            onConfigChange={setConfig}
            metadataSource={metadataSource}
            onMetadataSourceChange={chooseMetadataSource}
            showGuide={showGuide}
            lead={protocolField}
            identityFields={
              <Field>
                <Box horizontal={8} vertical={10}>
                  <Text variant="text-small-md">Provider ID</Text>
                </Box>
                <Input
                  value={config.providerId}
                  onChange={(e) =>
                    setConfig((f) => ({ ...f, providerId: e.target.value }))
                  }
                  required
                  pattern={SSO_PROVIDER_ID_PATTERN}
                  style={{ width: "100%" }}
                />
                <Box horizontal={8} top={6}>
                  <Text variant="text-small" color="var(--text-secondary)">
                    Part of the URLs below. It can’t be changed later.
                  </Text>
                </Box>
              </Field>
            }
          />
        </SettingsModalBody>

        <SettingsModalFooter style={{ gap: 8 }}>
          <Button size="l" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button size="l" type="submit" disabled={!!issuerError}>
            Add provider
          </Button>
        </SettingsModalFooter>
      </SsoModalForm>
      {confirmingPrimary && (
        <PrimaryChangeModal
          providerId={config.providerId.trim()}
          isPrimary
          confirmLabel="Add provider"
          onCancel={() => setConfirmingPrimary(false)}
          onConfirm={() => {
            setConfirmingPrimary(false);
            void register();
          }}
        />
      )}
    </Modal>
  );
}
